import React from "react";
import { Box, Typography, Button, Alert, CircularProgress, Stack, Chip } from "@mui/material";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import { paymentsApi } from "../Parent/Detail/payments/api";

/**
 * Return handler for GoCardless hosted bank setup. GoCardless redirects the
 * parent here after they complete (or exit) the hosted flow. Query params
 * usually include billing_request / billing_request_flow ids but we rely on
 * the webhook — not these params — for authoritative state. This page polls
 * our own API until the mandate is active (or a terminal failure).
 *
 * We don't trust what the URL says — the mandate is only ACTIVE when our
 * webhook store says so.
 */
const BankSetupComplete: React.FC = () => {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const parentId = params.get("parent") || "";

  const [status, setStatus] = React.useState<string>("pending_customer_approval");
  const [last4, setLast4] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [polling, setPolling] = React.useState<boolean>(true);
  const [attempts, setAttempts] = React.useState(0);

  React.useEffect(() => {
    if (!parentId) {
      setError("Missing parent reference. Please return to Step Up and refresh your Payments page.");
      setPolling(false);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        // Pull the parent's mandates and pick the most recent one. If it's
        // still pending, call refresh to force an authoritative GET.
        const list = await paymentsApi.bankMandates(parentId);
        const mandates = list?.data ?? [];
        const latest = Array.isArray(mandates) && mandates.length > 0 ? mandates[0] : null;
        if (!latest) {
          // No mandate rows yet — webhook may not have arrived. Keep polling.
        } else {
          const refreshed = await paymentsApi.refreshBankMandate(parentId, latest.id);
          const data = refreshed?.data ?? refreshed;
          if (cancelled) return;
          setStatus(data?.mandate_status ?? "pending_customer_approval");
          setLast4(data?.account_last4 ?? null);
          if (["active", "failed", "cancelled", "expired"].includes(data?.mandate_status)) {
            setPolling(false);
            return;
          }
        }
      } catch (e: any) {
        if (!cancelled && attempts > 24) {
          setError(e?.message || "Could not fetch mandate status.");
          setPolling(false);
        }
      }
      setAttempts((a) => a + 1);
    };
    tick();
    const h = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(h); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId]);

  const terminal = ["active", "failed", "cancelled", "expired"].includes(status);
  const isActive = status === "active" || status === "submitted";

  return (
    <Box sx={{ maxWidth: 560, mx: "auto", mt: 6, p: 3, borderRadius: 2, border: "1px solid #e5e7eb" }}>
      <Stack spacing={2} alignItems="center">
        {!terminal && (
          <>
            <HourglassBottomOutlinedIcon color="warning" fontSize="large" />
            <Typography variant="h6">Bank setup pending</Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Thanks — GoCardless is confirming your bank account. This usually
              takes less than a minute. You can leave this page open.
            </Typography>
            {polling && <CircularProgress size={28} sx={{ my: 1 }} />}
          </>
        )}

        {isActive && (
          <>
            <CheckCircleOutlineOutlinedIcon color="success" fontSize="large" />
            <Typography variant="h6">Bank account connected</Typography>
            <Chip
              label={last4 ? `Bank Account •••• ${last4} — GoCardless PAD` : "Mandate Active"}
              color="success"
            />
            <Typography variant="body2" color="text.secondary" align="center">
              Your mandate is active. Step Up can now collect scheduled monthly
              payments from this account. You can cancel the mandate at any
              time from your bank or by contacting the academy.
            </Typography>
          </>
        )}

        {terminal && !isActive && (
          <>
            <ErrorOutlineOutlinedIcon color="error" fontSize="large" />
            <Typography variant="h6">Setup not completed</Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              The bank setup finished with status <strong>{status}</strong>.
              Please contact the academy or try again.
            </Typography>
          </>
        )}

        {error && <Alert severity="error" sx={{ width: "100%" }}>{error}</Alert>}

        <Button variant="outlined" onClick={() => nav("/parent-portal/payments")}>
          Back to Payments
        </Button>
      </Stack>
    </Box>
  );
};

export default BankSetupComplete;
